<template>
    <ToggleSimple @update:open="toggleOpen = $event">
        <template #toggle>
            <div class="left">
                <div v-if="$te('fields.location.label')" class="toggle-text">
                    {{ $t('fields.location.label') }}
                    <span v-if="countProvinces.selected && countProvinces.selected > 0">{{ countProvinces.selected }}</span>
                    <span v-else-if="localCity && localCity !== ''">
                        <div class="icon-check-dark"></div>
                    </span>
                </div>
                <div v-if="$te(`fields.location.description`)" class="toggle-description">
                    {{ $t(`fields.location.description`) }}
                </div>
            </div>
            <div class="right">
                <div v-if="isActive" class="reset" @click="resetSection">
                    <div class="icon-reset-pos"></div>
                </div>
                <div class="toggle-icon"></div>
            </div>
        </template>

        <template #content>
            <div :class="cssClassOptions">
                <div v-if="!mode || mode === 'city'" class="location-city">
                    <div :class="['autosuggest-type-1', suggestions?.length > 0 ? 'active' : '']">
                        <div class="autosuggest-input">
                            <div class="input-wrap has-label">
                                <CustomInput
                                    :modelValue="searchQuery"
                                    inputKey="city_alt"
                                    :showDescription="showDescriptions"
                                    @update:modelValue="onSearchChanged"
                                    @keydown.down.prevent="highlightNext"
                                    @keydown.up.prevent="highlightPrevious"
                                    @keydown.enter.prevent="selectHighlighted"
                                />

                                <button @click.stop="clearCity()" :disabled="searchQuery === ''" class="btn-1 btn-s">
                                    <div class="icon-reset-neg"></div>
                                </button>
                            </div>

                            <div class="autosuggest-options" v-if="suggestions?.length > 0" ref="suggestionsList">
                                <ul @click.stop>
                                    <li 
                                        v-for="(suggestion, index) in suggestions" 
                                        :key="suggestion.term" 
                                        :class="{ 'highlighted': index === highlightedIndex }"
                                        @click="selectSuggestion(suggestion.term)"
                                    >
                                        {{ suggestion.term }}
                                    </li>
                                </ul> 
                            </div>
                        </div>
                    </div>

                    <CustomSelect
                        v-if="mode === 'city'"
                        :modelValue="localRadius"
                        inputKey="radius_alt"
                        @update:modelValue="onRadiusChanged"
                        :options="optionsRadius"
                        :showDescription="showDescriptions"
                    />
                </div>

                <div v-if="!mode || mode === 'province'" :class="['location-province', mode === 'province' ? 'active' : '']">
                    <CustomCheckbox
                        :modelValue="localProvince"
                        inputKey="province_alt"
                        @update:modelValue="onProvinceChanged"
                        :options="optionsProvince"
                        cssClass="custom-checkbox-type-5"
                        :showDescription="showDescriptions"
                    />
                </div>
            </div>
        </template>
    </ToggleSimple>
</template>

<script>
    import { debounce } from 'lodash';
    import { fetchSuggestions } from '@/services/freeApi';
    import { provinceMap } from '@/utils/maps/provinceMap';
    import i18n from "@/plugins/i18n";
    import ToggleSimple from '@/components/elements/misc/ToggleSimple';
    import CustomInput from '@/components/elements/inputs/CustomInput';
    import CustomCheckbox from '@/components/elements/inputs/CustomCheckbox';
    import CustomSelect from '@/components/elements/inputs/CustomSelect';

    export default {
        name: "OptionsLocation",
        components: {
            ToggleSimple,
            CustomInput,
            CustomCheckbox,
            CustomSelect
        },
        props: {
            modelValue: {
                type: Object,
                default: () => ({
                    province: [],
                    city: '',
                    radius: null,
                })
            },
            defaultVal: {
                type: Object,
                default: () => ({
                    province: [],
                    city: '',
                    radius: null,
                })
            },
            showDescriptions: {
                type: Boolean,
                default: true
            },
            cssClassOptions: {
                type: String,
                default: 'options-type-4'
            }
        },
        data() {
            return {
                name: 'company_location',
                toggleOpen: false,
                suggestions: [],
                highlightedIndex: -1,
                searchQuery: this.modelValue?.city || this.defaultVal?.city || '',
                localCity: this.modelValue?.city || this.defaultVal?.city || '',
                localRadius: this.modelValue?.radius || this.defaultVal?.radius || null,
                localProvince: Array.isArray(this.modelValue?.province) ? [...this.modelValue.province] : Array.isArray(this.defaultVal?.province) ? [...this.defaultVal.province] : []
            }
        },
        computed: {
            isActive() {
                return JSON.stringify(this.modelValue) !== JSON.stringify(this.defaultVal);
            },
            mode() {
                if (this.localProvince.length > 0) {
                    return 'province';
                } else if (this.localCity !== '') {
                    return 'city';
                } else {
                    return false;
                }
            },
            optionsProvince() {
                return provinceMap.map(option => ({
                    noTranslate: true,
                    text: option.text,
                    value: option.value,
                    icon: option.icon,
                    color: option.color
                }));
            },
            optionsRadius() {
                const distances = [0, 1, 2, 3, 4, 5, 10, 15, 25, 50];
                return distances.map(option => ({
                    noTranslate: true,
                    text: option === 0 ? i18n.global.t('fields.radius.option_all') : '+' + option + ' ' + i18n.global.t('fields.radius.option_suffix'),
                    value: option
                }));
            },
            countProvinces() {
                return {
                    total: this.optionsProvince.length,
                    selected: this.localProvince.length,
                    not_selected: this.optionsProvince.length - this.localProvince.length
                }
            }
        },
        watch: {
            modelValue: {
                handler(newVal) {
                    if (newVal) {
                        this.localCity = newVal.city || '';
                        this.searchQuery = newVal.city || this.searchQuery || '';
                        this.localRadius = newVal.radius || null;
                        this.localProvince = Array.isArray(newVal.province) ? [...newVal.province] : [];
                    }
                },
                deep: true
            }
        },
        methods: {
            resetSection(event = null) {
                if (event) {
                    event.stopPropagation();
                }
                this.searchQuery = '';
                this.localCity = '';
                this.localRadius = null;
                this.localProvince = [];
                this.emitUpdate();
            },
            clearCity() {
                this.searchQuery = '';
                this.localCity = '';
                this.emitUpdate();
                this.closeSuggestionList();
            },
            onSearchChanged: debounce(function(newValue) {
                this.localCity = '';
                this.emitUpdate();

                if(!newValue) {
                    this.searchQuery = '';
                    this.closeSuggestionList();
                } else {
                    this.searchQuery = newValue;
                    this.getSuggestions(newValue);
                }
            }, 300),
            async getSuggestions(newValue) {
                if(newValue.length < 1 || newValue.length > 10) {
                    this.suggestions = [];
                    return;
                }
                try {
                    const response = await fetchSuggestions({
                        type: 'city',
                        q: newValue
                    });
                    this.suggestions = response.data.suggest;
                } catch {
                    this.suggestions = [];
                } finally {
                    this.highlightedIndex = -1;
                }
            },
            selectSuggestion(term) {
                this.localCity = term;
                this.searchQuery = term;
                this.localProvince = [];
                this.emitUpdate();
                this.closeSuggestionList();
            },
            onProvinceChanged(newProvince) {
                this.localProvince = newProvince;
                this.localCity = '';
                this.localRadius = null;
                this.emitUpdate();
            },
            onRadiusChanged(newRadius) {
                this.localRadius = newRadius;
                this.localProvince = [];
                this.emitUpdate();
            },
            emitUpdate() {
                this.$emit('update:modelValue', { 
                    province: this.localProvince,
                    city: this.localCity,
                    radius: this.localRadius
                });
            },
            closeSuggestionList() {
                if (this.$refs.suggestionsList) {
                    this.suggestions = [];
                    this.highlightedIndex = -1;
                }
            },
            highlightNext() {
                if (this.highlightedIndex < this.suggestions.length - 1) {
                    this.highlightedIndex++;
                }
            },
            highlightPrevious() {
                if (this.highlightedIndex > 0) {
                    this.highlightedIndex--;
                }
            },
            selectHighlighted() {
                if (this.highlightedIndex >= 0 && this.highlightedIndex < this.suggestions.length) {
                    this.selectSuggestion(this.suggestions[this.highlightedIndex].term);
                }
            }
        },
        mounted() {
            document.addEventListener('click', this.closeSuggestionList);
        },
        unmounted() {
            document.removeEventListener('click', this.closeSuggestionList);
        },
    }
</script>